import type { ComputedRef } from 'vue'

import { usePage } from '@inertiajs/vue3'
import { computed } from 'vue'

import type { OrderMode } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'
import type { OrderSharedProps } from '@/domains/pjazza/types/pages.types.ts'

import { useOrderModeStore } from '@/domains/pjazza/store/use-order-mode-store/useOrderModeStore.ts'
import { useTabStore } from '@/domains/pjazza/store/use-tab-store/useTabStore.ts'
import { ORDER_MODE } from '@/domains/pjazza/types/consts/OrderMode.consts.ts'
import { ENTRY_MODE } from '@/domains/shared/types/consts/pjazza/EntryMode.consts.ts'

/** How a diner gets a table, where they have not got one. */
const JOIN_ACTION = {
  /** A printed code on the table — waiter-session venues. */
  SCAN: 'scan',
  /** Naming the table in the app — picker venues. */
  PICK: 'pick'
} as const

type JoinAction = (typeof JOIN_ACTION)[keyof typeof JOIN_ACTION]

interface OrderContext {
  /** What this order is right now — not what the venue offers. */
  mode: OrderMode
  /** The venue does both and no bill is open, so the mode is still the diner's. */
  canSwitch: boolean
  /** The way to a table, or null once there is one. */
  join: JoinAction | null
  /**
   * The table this device holds, whatever the mode. Only the mode control itself
   * should read this — it labels the dine-in segment, which stays "Table 4" while
   * takeaway is active because that is how the diner gets back.
   */
  tableName: string | null
  /**
   * The table, but only while it is actually in play. Null in takeaway mode:
   * switching to collection says the table is not part of this order, so nothing
   * else may go on advertising it — not the hero, not a bill link.
   */
  tableInPlay: string | null
  /** A bill exists behind the table; the mode is settled and the round appends to it. */
  hasOpenTab: boolean
  /** Chosen by the diner and not yet ordered onto, so it can still be corrected. */
  canChangeTable: boolean
  /** The one-line description of the mode, for wherever a caption is wanted. */
  statement: string
}

/**
 * Which mode stands, for a venue with no bill open yet.
 *
 * The diner's own choice only applies where the app can act on it. Picker mode
 * can: dine-in is a table they name here. A waiter-session venue cannot — dining
 * in is a printed code they have not scanned — so dine-in stays an action rather
 * than a state, and claiming otherwise would put the order on a path the backend
 * answers with a 404.
 */
const modeFor = (canSwitch: boolean, isPicker: boolean, chosen: OrderMode): OrderMode => {
  if (!canSwitch) return ORDER_MODE.DINE_IN

  if (isPicker) return chosen

  return ORDER_MODE.TAKEAWAY
}

const statementFor = (mode: OrderMode, tableName: string | null): string => {
  if (mode !== ORDER_MODE.DINE_IN) return 'Collect at the counter'

  return tableName ? `${tableName} · everyone here shares one bill` : 'At your table · everyone here shares one bill'
}

/**
 * What the order in front of the diner is, and what they can do about it.
 *
 * One resolver because the answer was previously derived twice — in the shell
 * and again on the menu page, in different spellings — and the two disagreed
 * about a table that had been picked but not yet ordered onto. Everything that
 * renders the mode reads this: the header control, the cart's toggle and
 * caption, and the confirm button's destination.
 *
 * It takes no arguments deliberately. Both call sites already hold the same two
 * sources, so passing them in would only give a caller the chance to pass
 * something else.
 */
export const useOrderContext = (): ComputedRef<OrderContext | null> => {
  const page = usePage<OrderSharedProps>()

  const tabStore = useTabStore()

  const orderMode = useOrderModeStore()

  return computed(() => {
    const site = page.props.site

    // The directory has no venue, so there is no order to describe.
    if (!site) return null

    const takesOrders = site.qrEnabled || site.takeawayEnabled

    if (!takesOrders) return null

    // No table ordering at all: a takeaway-only venue has one answer, and the
    // header says it rather than showing nothing, which is what it used to do.
    if (!site.qrEnabled) {
      return {
        mode: ORDER_MODE.TAKEAWAY,
        canSwitch: false,
        join: null,
        tableName: null,
        tableInPlay: null,
        hasOpenTab: false,
        canChangeTable: false,
        statement: statementFor(ORDER_MODE.TAKEAWAY, null)
      }
    }

    // A bill is open. The question was answered by sitting down, and rounds
    // append to that bill — so there is nothing left to choose here.
    if (tabStore.hasOpenTabFor(site.slug)) {
      const tableName = tabStore.tableNameFor(site.slug)

      return {
        mode: ORDER_MODE.DINE_IN,
        canSwitch: false,
        join: null,
        tableName,
        tableInPlay: tableName,
        hasOpenTab: true,
        canChangeTable: false,
        statement: statementFor(ORDER_MODE.DINE_IN, tableName)
      }
    }

    const isPicker = site.entryMode === ENTRY_MODE.TABLE_PICKER

    // Only picker mode leaves a table on the device before a tab exists; a
    // scanned code arrives as a session, which the branch above already took.
    const tableName = isPicker ? tabStore.tableNameFor(site.slug) : null

    const canSwitch = site.takeawayEnabled

    const mode = modeFor(canSwitch, isPicker, orderMode.mode)

    return {
      mode,
      canSwitch,
      // In picker mode the diner names the table; otherwise the printed code is
      // the only way in, so this is a scan either way it is offered.
      join: tableName === null ? (isPicker ? JOIN_ACTION.PICK : JOIN_ACTION.SCAN) : null,
      tableName,
      tableInPlay: mode === ORDER_MODE.DINE_IN ? tableName : null,
      hasOpenTab: false,
      canChangeTable: tableName !== null,
      statement: statementFor(mode, tableName)
    }
  })
}

export { JOIN_ACTION }
export type { JoinAction, OrderContext }
